import React, { useEffect, useState, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { auth, db } from '../firebase';

import { signOut, sendEmailVerification } from 'firebase/auth';

import {
  doc, getDoc, setDoc, serverTimestamp,
  collection, getDocs, query, where,
  updateDoc, addDoc,
} from 'firebase/firestore';

import './Home.css';

import packageJson from '../../package.json';

function Home({ verificationSuccess, setVerificationSuccess }) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Dashboard data
  const [recipeCount, setRecipeCount] = useState(null);
  const [favoritesCount, setFavoritesCount] = useState(null);
  const [todayMeals, setTodayMeals] = useState(null);
  const [shoppingCount, setShoppingCount] = useState(null);
  const [weekOverview, setWeekOverview] = useState(null);
  const [weekMealsDetailed, setWeekMealsDetailed] = useState(null);
  const [recentRecipes, setRecentRecipes] = useState(null);
  const [allRecipes, setAllRecipes] = useState(null);

  // Drawer (planification rapide)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMealType, setDrawerMealType] = useState(null);
  const [drawerSearch, setDrawerSearch] = useState('');
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerCategories, setDrawerCategories] = useState([]);
  const [drawerSelectedCats, setDrawerSelectedCats] = useState([]);

  const mealMeta = {
    breakfast: { label: 'Petit-déjeuner', icon: '🌅' },
    lunch:     { label: 'Déjeuner',       icon: '☀️' },
    dinner:    { label: 'Dîner',           icon: '🌙' },
  };

  useEffect(() => {
    const initUser = () => {
      if (auth.currentUser) {
        setUser(auth.currentUser);
        return true;
      } else {
        setLoading(false);
        return false;
      }
    };

    const fetchUserData = async () => {
      if (!auth.currentUser || !auth.currentUser.uid) {
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, 'Users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data && typeof data.name === 'string' && data.name.trim() !== '') {
            setUserName(data.name);
          }
          await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
        } else {
          const newUserData = {
            name: '',
            email: auth.currentUser.email,
            createdAt: serverTimestamp(),
            lastLogin: serverTimestamp(),
          };
          await setDoc(userRef, newUserData);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
      } finally {
        setLoading(false);
      }
    };

    const userExists = initUser();
    if (userExists) {
      fetchUserData();
      loadDashboardData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (verificationSuccess) {
      const timer = setTimeout(() => setVerificationSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [verificationSuccess, setVerificationSuccess]);

  const loadDashboardData = async () => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const weekStart = new Date(today);
    const dayOfWeek = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    try {
      const [recipesSnap, planningSnap, weekSnap, listsSnap, categoriesSnap, favoritesSnap] = await Promise.all([
        getDocs(collection(db, 'recettesUtilisateur', uid, 'recettes')),
        getDocs(query(collection(db, 'planningRepas', uid, 'plans'), where('date', '>=', today), where('date', '<', tomorrow))),
        getDocs(query(collection(db, 'planningRepas', uid, 'plans'), where('date', '>=', weekStart), where('date', '<=', weekEnd))),
        getDocs(collection(db, 'listesCourses', uid, 'listes')),
        getDocs(collection(db, 'categoriesUtilisateur', uid, 'categories')),
        getDocs(collection(db, 'recettesFavorites', uid, 'recettes')),
      ]);

      setFavoritesCount(favoritesSnap.size);

      // Catégories (pour le drawer)
      const cats = categoriesSnap.docs.map(c => ({ id: c.id, ...c.data() }));
      cats.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      setDrawerCategories(cats);

      setRecipeCount(recipesSnap.size);

      // Cache noms des recettes
      const recipeNameCache = {};
      recipesSnap.forEach(r => { recipeNameCache[r.id] = r.data().nom; });

      // Toutes les recettes (pour le drawer)
      const recipes = recipesSnap.docs.map(r => ({ id: r.id, nom: r.data().nom || 'Sans nom', categories: r.data().categories || [] }));
      recipes.sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
      setAllRecipes(recipes);

      // Repas du jour
      if (!planningSnap.empty) {
        const repas = planningSnap.docs[0].data().repas || {};
        const meals = ['breakfast', 'lunch', 'dinner']
          .filter(type => repas[type]?.idRecette)
          .map(type => ({
            type,
            ...mealMeta[type],
            recipeName: recipeNameCache[repas[type].idRecette] || '—',
            recipeId: repas[type].idRecette,
          }));
        setTodayMeals(meals);
      } else {
        setTodayMeals([]);
      }

      // Vue semaine (dot bar)
      const plannedDays = new Set();
      weekSnap.forEach(d => {
        const repas = d.data().repas || {};
        if (Object.keys(repas).length > 0) {
          const date = d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date);
          plannedDays.add(date.toDateString());
        }
      });
      const overview = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + i);
        overview.push({
          date: day,
          hasPlanning: plannedDays.has(day.toDateString()),
          isToday: day.toDateString() === today.toDateString(),
        });
      }
      setWeekOverview(overview);

      // Vue semaine détaillée (tableau desktop)
      const weekDetailMap = {};
      weekSnap.forEach(d => {
        const dateObj = d.data().date?.toDate ? d.data().date.toDate() : new Date(d.data().date);
        weekDetailMap[dateObj.toDateString()] = d.data().repas || {};
      });
      const detailed = [];
      for (let i = 0; i < 7; i++) {
        const day = new Date(weekStart);
        day.setDate(day.getDate() + i);
        const repas = weekDetailMap[day.toDateString()] || {};
        detailed.push({
          date: day,
          isToday: day.toDateString() === today.toDateString(),
          meals: {
            breakfast: repas.breakfast?.idRecette ? (recipeNameCache[repas.breakfast.idRecette] || 'Recette') : null,
            lunch:     repas.lunch?.idRecette     ? (recipeNameCache[repas.lunch.idRecette]     || 'Recette') : null,
            dinner:    repas.dinner?.idRecette    ? (recipeNameCache[repas.dinner.idRecette]    || 'Recette') : null,
          },
        });
      }
      setWeekMealsDetailed(detailed);

      // Recettes récentes
      const allRecipesForRecent = recipesSnap.docs.map(r => ({
        id: r.id,
        nom: r.data().nom || 'Sans nom',
        dateCreation: r.data().dateCreation?.toDate
          ? r.data().dateCreation.toDate()
          : (r.data().dateCreation ? new Date(r.data().dateCreation) : new Date(0)),
      }));
      allRecipesForRecent.sort((a, b) => b.dateCreation - a.dateCreation);
      setRecentRecipes(allRecipesForRecent.slice(0, 5));

      // Articles à acheter
      let unchecked = 0;
      listsSnap.forEach(listDoc => {
        const items = listDoc.data().articles || listDoc.data().items || [];
        unchecked += items.filter(i => !i.verifie && !i.checked).length;
      });
      setShoppingCount(unchecked);

    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
    }
  };

  // Ouvrir le drawer pour planifier un repas
  const openDrawer = (mealType) => {
    setDrawerMealType(mealType);
    setDrawerSearch('');
    setDrawerSelectedCats([]);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerMealType(null);
    setDrawerSearch('');
    setDrawerSelectedCats([]);
  };

  // Sauvegarder le repas sélectionné
  const selectRecipeForMeal = async (recipe) => {
    if (!auth.currentUser || !drawerMealType || drawerSaving) return;
    setDrawerSaving(true);

    const uid = auth.currentUser.uid;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    try {
      const planCol = collection(db, 'planningRepas', uid, 'plans');
      const q = query(planCol, where('date', '>=', today), where('date', '<', tomorrow));
      const snap = await getDocs(q);

      if (!snap.empty) {
        const planRef = doc(planCol, snap.docs[0].id);
        const existing = snap.docs[0].data();
        await updateDoc(planRef, {
          repas: {
            ...(existing.repas || {}),
            [drawerMealType]: { idRecette: recipe.id, nombrePersonnes: 1 },
          },
          dateMiseAJour: serverTimestamp(),
        });
      } else {
        await addDoc(planCol, {
          date: today,
          repas: { [drawerMealType]: { idRecette: recipe.id, nombrePersonnes: 1 } },
          notes: '',
          dateCreation: serverTimestamp(),
          dateMiseAJour: serverTimestamp(),
        });
      }

      // Mise à jour optimiste
      setTodayMeals(prev => {
        const filtered = (prev || []).filter(m => m.type !== drawerMealType);
        return [...filtered, {
          type: drawerMealType,
          ...mealMeta[drawerMealType],
          recipeName: recipe.nom,
          recipeId: recipe.id,
        }];
      });

      // Mise à jour du tableau semaine
      setWeekMealsDetailed(prev => prev ? prev.map(day =>
        day.isToday
          ? { ...day, meals: { ...day.meals, [drawerMealType]: recipe.nom } }
          : day
      ) : prev);

      closeDrawer();
    } catch (err) {
      console.error('Erreur lors de la planification:', err);
    } finally {
      setDrawerSaving(false);
    }
  };

  const filteredRecipes = useMemo(() => {
    if (!allRecipes) return [];
    let list = allRecipes;
    const term = drawerSearch.trim().toLowerCase();
    if (term) list = list.filter(r => r.nom.toLowerCase().includes(term));
    if (drawerSelectedCats.length > 0) {
      list = list.filter(r => drawerSelectedCats.some(catId => (r.categories || []).includes(catId)));
    }
    return list;
  }, [allRecipes, drawerSearch, drawerSelectedCats]);

  const handleLogout = async () => {
    try { await signOut(auth); } catch (err) { console.error(err); }
  };

  const handleSendVerificationEmail = async () => {
    setVerificationError('');
    setVerificationSent(false);
    try {
      await sendEmailVerification(auth.currentUser);
      setVerificationSent(true);
    } catch (error) {
      setVerificationError("Impossible d'envoyer l'email de vérification. Veuillez réessayer plus tard.");
    }
  };

  const formatToday = () => new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  const navigateToAddRecipe = () => navigate('/add-recipe');
  const navigateToRecipesList = () => navigate('/recipes');
  const navigateToMealPlanner = () => navigate('/meal-planner');
  const navigateToShoppingList = () => navigate('/shopping-list');
  const navigateToSettings = () => { navigate('/settings'); setShowUserMenu(false); };
  const toggleUserMenu = () => setShowUserMenu(v => !v);

  useEffect(() => {
    const handleClickOutside = event => {
      if (showUserMenu && !event.target.closest('.user-menu') && !event.target.closest('.user-dropdown')) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Fermer drawer avec Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const handleKey = e => { if (e.key === 'Escape') closeDrawer(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [drawerOpen]);

  if (loading) {
    return <div className="loading">Chargement des données utilisateur...</div>;
  }

  const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="home-container">
      <header className="home-header">
        <div className="home-header-inner">
        <h1>Bobo Food Plan</h1>

        <div className="header-actions">
          <div className="user-menu">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="32" height="32" fill="#ff7043"
              className="user-icon" onClick={toggleUserMenu} style={{ cursor: 'pointer' }}>
              <path d="M399 384.2C376.9 345.8 335.4 320 288 320H224c-47.4 0-88.9 25.8-111 64.2c35.2 39.2 86.2 63.8 143 63.8s107.8-24.7 143-63.8zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm256 16a72 72 0 1 0 0-144 72 72 0 1 0 0 144z" />
            </svg>

            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-info">
                  <span className="user-name">{userName || 'Utilisateur'}</span>
                  <span className="user-email">{user?.email}</span>
                </div>
                <div className="menu-divider"></div>
                <button className="menu-item" onClick={navigateToSettings}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  Paramètres
                </button>
                <div className="menu-divider"></div>
                <button className="menu-item logout-item" onClick={handleLogout}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </header>

      {verificationSuccess && (
        <div className="verification-success-message">
          <p><strong>Votre adresse email a été vérifiée avec succès!</strong> Vous pouvez maintenant accéder à toutes les fonctionnalités de l'application.</p>
        </div>
      )}

      {user && !user.emailVerified && (
        <div className="email-verification-alert">
          <p><strong>Votre adresse email n'est pas vérifiée.</strong> Certaines fonctionnalités pourraient être limitées.</p>
          {verificationSent ? (
            <p className="verification-success">Un nouvel email de vérification a été envoyé à {user.email}. Veuillez vérifier votre boîte de réception et vos spams.</p>
          ) : (
            <button onClick={handleSendVerificationEmail} className="verification-button">Renvoyer l'email de vérification</button>
          )}
          {verificationError && <p className="verification-error">{verificationError}</p>}
        </div>
      )}

      {/* GREETING */}
      <div className="dashboard-greeting">
        <div>
          <h2 className="greeting-name">Bonjour{userName ? `, ${userName}` : ''} 👋</h2>
          <p className="greeting-date">{formatToday()}</p>
        </div>
        {recipeCount > 0 && (
          <button className="quick-add-btn" onClick={navigateToAddRecipe}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Recette
          </button>
        )}
      </div>

      {/* BODY */}
      <div className="dashboard-body">

        {/* MOBILE ONLY — Onboarding (première recette) */}
        {recipeCount === 0 && (
          <div className="onboarding-card mobile-only">
            <div className="onboarding-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
            </div>
            <h3 className="onboarding-title">Par où commencer ?</h3>
            <p className="onboarding-text">
              Créez votre première recette ou explorez le catalogue pour importer des recettes prêtes à l'emploi.
            </p>
            <button className="onboarding-cta" onClick={navigateToAddRecipe}>
              Créer ma première recette
            </button>
            <button className="onboarding-cta-secondary" onClick={() => navigate('/catalogue')}>
              📚 Explorer le catalogue
            </button>
          </div>
        )}

        {/* LIGNE 1 COL 1 — Today */}
        <section className="today-section">
          <div className="section-title-row">
            <h3>Aujourd'hui</h3>
            <button className="section-link-btn" onClick={navigateToMealPlanner}>Planning complet →</button>
          </div>
          <div className="today-meals-grid">
            {['breakfast', 'lunch', 'dinner'].map(type => {
              const meta = mealMeta[type];
              const meal = todayMeals?.find(m => m.type === type);
              return (
                <div
                  key={type}
                  className={`today-meal-card ${meal ? 'tmc-filled' : 'tmc-empty'}`}
                  onClick={() => meal ? navigate(`/recipe/${meal.recipeId}`) : openDrawer(type)}
                >
                  <span className="tmc-icon">{meta.icon}</span>
                  <span className="tmc-type">{meta.label}</span>
                  {todayMeals === null
                    ? <span className="tmc-placeholder">…</span>
                    : meal
                      ? <span className="tmc-recipe">{meal.recipeName}</span>
                      : <span className="tmc-add">+ Planifier</span>
                  }
                </div>
              );
            })}
          </div>
        </section>

        {/* LIGNE 1 COL 2 — Vue d'ensemble (desktop uniquement) */}
        <div className="sidebar-card sidebar-overview desktop-only">
          <h3 className="sidebar-card-title">Vue d'ensemble</h3>
          <div className="home-stats-grid">
            <div className="stat-item" onClick={navigateToRecipesList}>
              <span className="stat-number">{recipeCount ?? '—'}</span>
              <span className="stat-label">Recettes</span>
            </div>
            <div className="stat-item" onClick={() => navigate('/recipes', { state: { filterFavorites: true } })}>
              <span className="stat-number">{favoritesCount ?? '—'}</span>
              <span className="stat-label">Favoris</span>
            </div>
          </div>
          <button className="overview-recipes-link" onClick={navigateToRecipesList}>
            Voir mes recettes
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13" style={{marginLeft: '4px'}}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
          <button className="overview-recipes-link" onClick={() => navigate('/catalogue')} style={{marginTop: '6px'}}>
            Catalogue de recettes
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13" style={{marginLeft: '4px'}}>
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        {/* MOBILE ONLY — Dot bar */}
        <section className="week-section mobile-only" onClick={navigateToMealPlanner}>
          <div className="section-title-row">
            <h3>Cette semaine</h3>
            {weekOverview && (
              <span className="week-count">{weekOverview.filter(d => d.hasPlanning).length}/7 jours planifiés</span>
            )}
          </div>
          <div className="week-bar">
            {(weekOverview ?? Array(7).fill(null)).map((day, i) => (
              <div key={i} className={`week-day ${day?.isToday ? 'wd-today' : ''} ${day?.hasPlanning ? 'wd-planned' : ''}`}>
                <span className="wd-label">{DAY_LABELS[i][0]}</span>
                <div className="wd-dot"></div>
                <span className="wd-date">{day ? day.date.getDate() : ''}</span>
              </div>
            ))}
          </div>
        </section>

        {/* MOBILE ONLY — Quick stats */}
        <div className="quick-stats-row mobile-only">
          <div className="qs-item" onClick={navigateToRecipesList}>
            <span className="qs-number">{recipeCount ?? '—'}</span>
            <span className="qs-label">recette{recipeCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="qs-divider" />
          <div className="qs-item" onClick={() => navigate('/recipes', { state: { filterFavorites: true } })}>
            <span className="qs-number">{favoritesCount ?? '—'}</span>
            <span className="qs-label">favori{favoritesCount !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* MOBILE ONLY — Catalogue */}
        <div className="mobile-shopping-card mobile-only" onClick={() => navigate('/catalogue')}>
          <div className="mobile-shopping-left">
            <span className="mobile-shopping-icon">📚</span>
            <div>
              <div className="mobile-shopping-title">Catalogue de recettes</div>
              <div className="mobile-shopping-count">Découvrez de nouvelles recettes</div>
            </div>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>

        {/* MOBILE ONLY — Liste de courses */}
        <div className="mobile-shopping-card mobile-only" onClick={navigateToShoppingList}>
          <div className="mobile-shopping-left">
            <span className="mobile-shopping-icon">🛒</span>
            <div>
              <div className="mobile-shopping-title">Liste de courses</div>
              {shoppingCount > 0 && (
                <div className="mobile-shopping-count">{shoppingCount} article{shoppingCount > 1 ? 's' : ''} à acheter</div>
              )}
              {shoppingCount === 0 && (
                <div className="mobile-shopping-count">Aucun article en attente</div>
              )}
            </div>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>

        {/* LIGNE 2 COL 1 — Planning semaine (desktop uniquement) */}
        <section className="week-detail-section desktop-only">
          <div className="section-title-row">
            <h3>Planning de la semaine</h3>
            {weekOverview && (
              <span className="week-count">{weekOverview.filter(d => d.hasPlanning).length}/7 jours planifiés</span>
            )}
          </div>
          <div className="wdt-header">
            <div></div>
            <div className="wdt-header-cell">Matin</div>
            <div className="wdt-header-cell">Midi</div>
            <div className="wdt-header-cell">Soir</div>
          </div>
          {(weekMealsDetailed ?? Array(7).fill(null)).map((day, i) => (
            <div key={i} className={`wdt-row ${day?.isToday ? 'wdt-today' : ''}`}>
              <div className="wdt-day-info">
                <span className="wdt-day-name">{DAY_LABELS[i]}</span>
                <span className="wdt-day-date">{day ? day.date.getDate() : ''}</span>
              </div>
              <div className={`wdt-slot ${day?.meals?.breakfast ? 'wdt-slot-filled' : 'wdt-slot-empty'}`}>
                {day?.meals?.breakfast ?? '—'}
              </div>
              <div className={`wdt-slot ${day?.meals?.lunch ? 'wdt-slot-filled' : 'wdt-slot-empty'}`}>
                {day?.meals?.lunch ?? '—'}
              </div>
              <div className={`wdt-slot ${day?.meals?.dinner ? 'wdt-slot-filled' : 'wdt-slot-empty'}`}>
                {day?.meals?.dinner ?? '—'}
              </div>
            </div>
          ))}
        </section>

        {/* LIGNE 2 COL 2 — Sidebar bas (desktop uniquement) */}
        <div className="sidebar-bottom desktop-only">
          {recipeCount === 0 ? (
            <div className="sidebar-card onboarding-card">
              <div className="onboarding-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                </svg>
              </div>
              <h3 className="onboarding-title">Par où commencer ?</h3>
              <p className="onboarding-text">
                Créez votre première recette ou explorez le catalogue pour importer des recettes prêtes à l'emploi.
              </p>
              <button className="onboarding-cta" onClick={navigateToAddRecipe}>
                Créer ma première recette
              </button>
              <button className="onboarding-cta-secondary" onClick={() => navigate('/catalogue')}>
                📚 Explorer le catalogue
              </button>
            </div>
          ) : (
            <div className="sidebar-card">
              <h3 className="sidebar-card-title">Recettes récentes</h3>
              <div className="recent-recipes-list">
                {recentRecipes === null
                  ? <p className="rr-empty">Chargement…</p>
                  : recentRecipes.map(r => (
                      <div key={r.id} className="rr-item" onClick={() => navigate(`/recipe/${r.id}`)}>
                        <span className="rr-name">{r.nom}</span>
                        <svg className="rr-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </div>
                    ))
                }
              </div>
            </div>
          )}
          <div className="sidebar-card">
            <h3 className="sidebar-card-title">Liste de courses</h3>
            <button className="sidebar-shopping-btn" onClick={navigateToShoppingList}>
              Voir ma liste de courses
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" style={{marginLeft: '4px'}}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>

      </div>{/* fin dashboard-body */}

      <footer className="home-footer">
        <p>Version {packageJson.version} - Bobo Food Plan</p>
      </footer>

      {/* DRAWER — Planification rapide */}
      {drawerOpen && (
        <>
          <div className="drawer-overlay" onClick={closeDrawer} />
          <div className="home-drawer">
            <div className="drawer-header">
              <div className="drawer-title">
                <span className="drawer-title-icon">{drawerMealType ? mealMeta[drawerMealType].icon : ''}</span>
                <span>Planifier : {drawerMealType ? mealMeta[drawerMealType].label : ''}</span>
              </div>
              <button className="drawer-close" onClick={closeDrawer} aria-label="Fermer">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="drawer-search-wrapper">
              <svg className="drawer-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                className="drawer-search-input"
                placeholder="Rechercher une recette…"
                value={drawerSearch}
                onChange={e => setDrawerSearch(e.target.value)}
              />
              {drawerSearch && (
                <button className="drawer-search-clear" onClick={() => setDrawerSearch('')}>×</button>
              )}
            </div>

            {drawerCategories.length > 0 && (
              <div className="drawer-category-chips">
                <button
                  className={`drawer-chip ${drawerSelectedCats.length === 0 ? 'drawer-chip-all-active' : 'drawer-chip-all'}`}
                  onClick={() => setDrawerSelectedCats([])}
                >
                  Tout
                </button>
                {drawerCategories.map(cat => {
                  const active = drawerSelectedCats.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      className={`drawer-chip ${active ? 'drawer-chip-active' : ''}`}
                      style={active
                        ? { backgroundColor: cat.couleur, borderColor: cat.couleur, color: '#fff' }
                        : { borderColor: cat.couleur + '99', color: cat.couleur }
                      }
                      onClick={() => setDrawerSelectedCats(prev =>
                        active ? prev.filter(id => id !== cat.id) : [...prev, cat.id]
                      )}
                    >
                      {cat.nom}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="drawer-recipe-list">
              {allRecipes === null ? (
                <p className="drawer-empty">Chargement…</p>
              ) : filteredRecipes.length === 0 ? (
                <p className="drawer-empty">Aucune recette trouvée</p>
              ) : (
                filteredRecipes.map(recipe => (
                  <button
                    key={recipe.id}
                    className="drawer-recipe-item"
                    onClick={() => selectRecipeForMeal(recipe)}
                    disabled={drawerSaving}
                  >
                    <span className="drawer-recipe-name">{recipe.nom}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Home;
